import React, { useState, useRef, useCallback, useEffect } from 'react';
import { updateProfile, deletePhoto } from '../../store/slices/profile';
import { uploadImage } from '../../utils/helpers/fileupload';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify"
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';

const ASPECT_RATIOS = [
    { label: 'Free', value: null },
    { label: '1:1', value: 1 },
    { label: '4:5', value: 4 / 5 },
    { label: '3:4', value: 3 / 4 },
    { label: '16:9', value: 16 / 9 },
    { label: '9:16', value: 9 / 16 },
];

const FRAME_SIZE = 420;
const MAX_PHOTOS = 10;

const ImageUpload = () => {
    const [existingPhotos, setExistingPhotos] = useState([]);

    const profileData = useSelector(
        (state) => state.profile.profilelist
    );
    useEffect(() => {
        if (profileData?.profile?.photos?.length) {
            setExistingPhotos(profileData.profile.photos);
        }
    }, [profileData]);
    const profilePhoto = profileData?.profilePhoto;
    const orderedPhotos = [
        ...(profilePhoto ? [profilePhoto] : []),
        ...existingPhotos.filter(
            (photo) => photo !== profilePhoto
        )
    ];
    const token = localStorage.getItem("accessToken");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showViewer, setShowViewer] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [originalImageSrc, setOriginalImageSrc] = useState(null);
    const [step, setStep] = useState('upload');
    const [selectedRatio, setSelectedRatio] = useState(ASPECT_RATIOS[1]);
    const [showCropModal, setShowCropModal] = useState(false);

    // Multi-photo state
    const [photoQueue, setPhotoQueue] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [croppedImages, setCroppedImages] = useState([]);
    const [uploadStatus, setUploadStatus] = useState([]);
    const [allUploaded, setAllUploaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitDone, setSubmitDone] = useState(false);

    // Crop state
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });

    const imgRef = useRef(null);
    const frameRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    const getFrameDims = useCallback(() => {
        const ratio = selectedRatio.value;
        if (!ratio) return { w: FRAME_SIZE, h: FRAME_SIZE };
        if (ratio >= 1) return { w: FRAME_SIZE, h: Math.round(FRAME_SIZE / ratio) };
        return { w: Math.round(FRAME_SIZE * ratio), h: FRAME_SIZE };
    }, [selectedRatio]);

    const frameDims = getFrameDims();

    const fitImageToFrame = useCallback((natW, natH, fw, fh) => {
        const scaleW = fw / natW;
        const scaleH = fh / natH;
        const fitScale = Math.max(scaleW, scaleH);
        setZoom(fitScale);
        setOffset({ x: 0, y: 0 });
    }, []);

    useEffect(() => {
        if (imgNatural.w && imgNatural.h) {
            fitImageToFrame(imgNatural.w, imgNatural.h, frameDims.w, frameDims.h);
        }
    }, [selectedRatio, imgNatural]);


    useEffect(() => {
        if (step === 'crop' && photoQueue[currentIndex]) {
            setOriginalImageSrc(photoQueue[currentIndex].dataUrl);
            setSelectedRatio(ASPECT_RATIOS[1]);
        }
    }, [currentIndex, step]);


    const onSelectFile = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const limited = files.slice(0, MAX_PHOTOS);
        const readers = limited.map(
            (file) =>
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve({ file, dataUrl: reader.result });
                    reader.readAsDataURL(file);
                })
        );

        Promise.all(readers).then((items) => {
            setPhotoQueue(items);
            setCroppedImages([]);
            setUploadStatus([]);
            setAllUploaded(false);
            setSubmitDone(false);
            setCurrentIndex(0);
            setOriginalImageSrc(items[0].dataUrl);
            setStep('crop');
            setShowCropModal(true);
        });
    };

    const onImageNaturalLoad = (e) => {
        const { naturalWidth: w, naturalHeight: h } = e.target;
        setImgNatural({ w, h });
        fitImageToFrame(w, h, frameDims.w, frameDims.h);
    };


    const clampOffset = useCallback((ox, oy, z, fw, fh, nw, nh) => {
        const scaledW = nw * z;
        const scaledH = nh * z;
        const maxX = (scaledW - fw) / 2;
        const maxY = (scaledH - fh) / 2;
        return {
            x: Math.max(-maxX, Math.min(maxX, ox)),
            y: Math.max(-maxY, Math.min(maxY, oy)),
        };
    }, []);


    const onMouseDown = (e) => {
        e.preventDefault();
        setDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const onMouseMove = useCallback((e) => {
        if (!dragging) return;
        const raw = { x: e.clientX - dragStart.x, y: e.clientY - dragStart.y };
        const clamped = clampOffset(raw.x, raw.y, zoom, frameDims.w, frameDims.h, imgNatural.w, imgNatural.h);
        setOffset(clamped);
    }, [dragging, dragStart, zoom, frameDims, imgNatural, clampOffset]);

    const onMouseUp = () => setDragging(false);


    const onTouchStart = (e) => {
        const t = e.touches[0];
        setDragging(true);
        setDragStart({ x: t.clientX - offset.x, y: t.clientY - offset.y });
    };

    const onTouchMove = (e) => {
        if (!dragging) return;
        const t = e.touches[0];
        const raw = { x: t.clientX - dragStart.x, y: t.clientY - dragStart.y };
        const clamped = clampOffset(raw.x, raw.y, zoom, frameDims.w, frameDims.h, imgNatural.w, imgNatural.h);
        setOffset(clamped);
    };

    const handleZoomChange = (val) => {
        const minZoom = Math.max(frameDims.w / imgNatural.w, frameDims.h / imgNatural.h);
        const newZoom = Math.max(minZoom, val);
        const clamped = clampOffset(offset.x, offset.y, newZoom, frameDims.w, frameDims.h, imgNatural.w, imgNatural.h);
        setZoom(newZoom);
        setOffset(clamped);
    };


    const buildCroppedDataUrl = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { w: fw, h: fh } = frameDims;
        const { w: nw, h: nh } = imgNatural;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = fw * dpr;
        canvas.height = fh * dpr;
        ctx.scale(dpr, dpr);

        const scaledW = nw * zoom;
        const scaledH = nh * zoom;
        const imgX = (fw - scaledW) / 2 + offset.x;
        const imgY = (fh - scaledH) / 2 + offset.y;

        const img = imgRef.current;
        ctx.drawImage(img, imgX, imgY, scaledW, scaledH);

        // Watermark
        const fs = Math.max(12, Math.floor(fw * 0.04));
        const barH = fs + 22;

        ctx.save();
        ctx.fillStyle = 'rgba(10,8,5,0.78)';
        ctx.fillRect(0, fh - barH, fw, barH);
        ctx.restore();

        ctx.save();
        ctx.fillStyle = 'rgba(201,168,76,0.9)';
        ctx.fillRect(fw - 3, fh - barH, 3, barH);
        ctx.restore();

        ctx.save();
        ctx.font = `700 ${fs}px 'DM Sans', sans-serif`;
        const kvlW = ctx.measureText('KVL').width;
        ctx.restore();

        ctx.save();
        ctx.font = `400 ${Math.max(10, fs - 1)}px 'DM Sans', sans-serif`;
        const matW = ctx.measureText('Matrimony').width;
        ctx.restore();

        const gap = 6;
        const totalW = kvlW + gap + matW;
        const startX = fw - totalW - 14;

        ctx.save();
        ctx.font = `700 ${fs}px 'DM Sans', sans-serif`;
        ctx.fillStyle = 'rgba(240,220,160,0.98)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 6;
        ctx.fillText('KVL', startX, fh - barH / 2);
        ctx.restore();

        ctx.save();
        ctx.font = `400 ${Math.max(10, fs - 1)}px 'DM Sans', sans-serif`;
        ctx.fillStyle = 'rgba(210,185,120,0.88)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 4;
        ctx.fillText('Matrimony', startX + kvlW + gap, fh - barH / 2);
        ctx.restore();

        return canvas.toDataURL('image/jpeg', 0.92);
    };


    const handleApplyCrop = () => {
        const dataUrl = buildCroppedDataUrl();
        const updated = [...croppedImages, { dataUrl, uploadedUrl: null }];
        setCroppedImages(updated);

        const nextIndex = currentIndex + 1;
        if (nextIndex < photoQueue.length) {
            setCurrentIndex(nextIndex);

        } else {

            setShowCropModal(false);

            setUploadStatus(updated.map(() => 'pending'));
            setStep('progress');
            runUploads(updated);
        }
    };


    const runUploads = async (images) => {
        const statuses = images.map(() => 'pending');
        const urls = images.map(() => null);

        for (let i = 0; i < images.length; i++) {
            statuses[i] = 'uploading';
            setUploadStatus([...statuses]);

            try {
                const res = await fetch(images[i].dataUrl);
                const blob = await res.blob();
                const file = new File([blob], `kvl-photo-${i + 1}.jpg`, { type: 'image/jpeg' });

                const uploadedUrl = await (uploadImage(file, token));
                urls[i] = uploadedUrl;
                statuses[i] = 'done';

                setCroppedImages((prev) =>
                    prev.map((item, idx) => (idx === i ? { ...item, uploadedUrl } : item))
                );

            } catch (err) {
                statuses[i] = 'error';
                toast.error(err || err?.response?.data?.message || err?.message || "Image upload failed");
            }

            setUploadStatus([...statuses]);
        }

        setAllUploaded(true);
    };


    const handleSubmitPhotos = async () => {
        setSubmitting(true);

        const photos = [
            ...existingPhotos,
            ...croppedImages
                .map((img) => img.uploadedUrl)
                .filter(Boolean)
        ];

        try {
            const res = await dispatch(updateProfile({ photos })).unwrap();

            toast.success(res?.message || "Image uploaded successfully");

            setSubmitDone(true);

            setTimeout(() => {
                window.location.reload();
            }, 1200);

        } catch (err) {
            toast.error(
                typeof err === "string"
                    ? err
                    : err?.message ||
                    err?.response?.data?.message ||
                    "Something went wrong"
            );
        } finally {
            setSubmitting(false);
        }
    };


    const reset = () => {
        setOriginalImageSrc(null);
        setStep('upload');
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setPhotoQueue([]);
        setCroppedImages([]);
        setUploadStatus([]);
        setCurrentIndex(0);
        setAllUploaded(false);
        setSubmitDone(false);
        setSubmitting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };


    const StatusIcon = ({ status }) => {
        if (status === 'pending') return <span className="pv-status-dot pv-status-pending" />;
        if (status === 'uploading') return <span className="pv-status-dot pv-status-uploading pv-spin" />;
        if (status === 'done') return (
            <svg className="pv-status-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
        if (status === 'error') return (
            <svg className="pv-status-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
        return null;
    };

    const handleMakePrimary = async (imageUrl) => {
        try {
            const res = await dispatch(
                updateProfile({
                    profilePhoto: imageUrl
                })
            ).unwrap();

            toast.success(
                res?.message || "Profile photo updated"
            );

        } catch (err) {
            toast.error(
                err ||
                "Failed to update profile photo"
            );
        }
    };
    const handleDeletePhoto = async (imageUrl) => {
        try {
            const res = await dispatch(
                deletePhoto({
                    photo: imageUrl,
                })
            ).unwrap();

            toast.success(
                res?.message || "Photo deleted successfully"
            );

            // Update UI without reloading
            setExistingPhotos(res.data.photos);

            if (showViewer) {
                setShowViewer(false);
            }

        } catch (err) {
            toast.error(
                err?.message ||
                err ||
                "Unable to delete photo"
            );
        }
    };

    return (
        <>
            <div className="pv-root">
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onSelectFile}
                    style={{ display: "none" }}
                />
                {originalImageSrc && (
                    <img
                        ref={imgRef}
                        src={originalImageSrc}
                        alt="source"
                        onLoad={onImageNaturalLoad}
                        style={{ display: 'none' }}
                        crossOrigin="anonymous"
                    />
                )}

                <div className="pv-card">

                    {/* Header */}
                    <div className="pv-header">

                        <div>
                            <h1 className="pv-title"> Photos</h1>

                        </div>
                    </div>
                    {step === "upload" && (
                        <div className="photo-viewer">
                            {orderedPhotos.map((image, index) => (
                                <div
                                    className="photo-card"
                                    key={index}
                                    onClick={() => {
                                        setViewerIndex(index);
                                        setShowViewer(true);
                                    }}
                                >
                                    <img
                                        src={image}
                                        alt={`Profile ${index + 1}`}
                                        className="photo-card-img"
                                    />

                                    {image === profilePhoto && (
                                        <span className="primary-photo-badge">
                                            ★ Primary
                                        </span>
                                    )}

                                    {image !== profilePhoto && (
                                        <button
                                            className="make-primary-btn"
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevents image viewer from opening
                                                handleMakePrimary(image);
                                            }}
                                        >
                                            Set as Primary
                                        </button>
                                    )}
                                    <button
                                        className="delete-photo-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                                window.confirm(
                                                    "Are you sure you want to delete this photo?"
                                                )
                                            ) {
                                                handleDeletePhoto(image);
                                            }
                                        }}
                                    >
                                        <FaTrash size={14} />
                                    </button>
                                </div>
                            ))}

                            {orderedPhotos.length < MAX_PHOTOS && (
                                <div
                                    className="upload-card"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="upload-plus">+</div>
                                    <span>Add Photo</span>
                                </div>
                            )}
                        </div>
                    )}

                    <Modal
                        show={showCropModal}
                        onHide={() => {
                            setShowCropModal(false);
                            reset();
                        }}
                        centered
                        size="l"
                        backdrop="static"
                    >


                        <Modal.Body>

                            <div className="pv-crop-section">

                                <div className="pv-crop-counter">

                                    <div className="pv-crop-dots mx-3">
                                        {photoQueue.map((_, i) => (
                                            <span
                                                key={i}
                                                className={`pv-crop-dot ${i < currentIndex
                                                    ? "done"
                                                    : i === currentIndex
                                                        ? "active"
                                                        : ""
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="pv-frame-wrapper">
                                    <div
                                        ref={frameRef}
                                        className="pv-frame"
                                        style={{ width: frameDims.w, height: frameDims.h }}
                                        onMouseMove={onMouseMove}
                                        onMouseUp={onMouseUp}
                                        onMouseLeave={onMouseUp}
                                        onTouchMove={onTouchMove}
                                        onTouchEnd={onMouseUp}
                                    >
                                        {originalImageSrc && (
                                            <img
                                                src={originalImageSrc}
                                                alt="crop"
                                                className="pv-crop-img"
                                                style={{
                                                    width: imgNatural.w * zoom,
                                                    height: imgNatural.h * zoom,
                                                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                                                    cursor: dragging ? 'grabbing' : 'grab',
                                                }}
                                                onMouseDown={onMouseDown}
                                                onTouchStart={onTouchStart}
                                                draggable={false}
                                            />
                                        )}
                                        <div className="pv-frame-grid" />
                                        <div className="pv-frame-corners">
                                            <span /><span /><span /><span />
                                        </div>
                                    </div>
                                </div>

                                {/* Zoom */}
                                <div className="pv-zoom-row">
                                    <svg className="pv-zoom-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35M11 8v6M8 11h6" strokeLinecap="round" />
                                    </svg>
                                    <input
                                        type="range"
                                        className="pv-zoom-slider"
                                        min={Math.max(frameDims.w / (imgNatural.w || 1), frameDims.h / (imgNatural.h || 1))}
                                        max={4}
                                        step={0.01}
                                        value={zoom}
                                        onChange={(e) => handleZoomChange(Number(e.target.value))}
                                    />
                                    <span className="pv-zoom-val">{Math.round(zoom * 100)}%</span>
                                </div>

                                {/* Aspect ratios */}
                                <div className="pv-ratio-row">
                                    {ASPECT_RATIOS.map((r) => (
                                        <button
                                            key={r.label}
                                            className={`pv-ratio-btn ${selectedRatio.label === r.label ? 'active' : ''}`}
                                            onClick={() => setSelectedRatio(r)}
                                        >
                                            {r.label}
                                        </button>
                                    ))}
                                </div>

                            </div>

                        </Modal.Body>

                        <Modal.Footer>
                            <button
                                className="pv-btn-ghost"
                                onClick={() => {
                                    setShowCropModal(false);
                                    reset();
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="pv-btn-primary"
                                onClick={handleApplyCrop}
                            >
                                {currentIndex + 1 < photoQueue.length
                                    ? "Crop"
                                    : "Apply"}
                            </button>
                        </Modal.Footer>
                    </Modal>

                    <Modal
                        show={showViewer}
                        onHide={() => setShowViewer(false)}
                        centered
                        size="lg"
                    >
                        <Modal.Body className="text-center">

                            <img
                                src={orderedPhotos[viewerIndex]}
                                alt=""
                                className="viewer-image"
                            />

                            {viewerIndex === 0 && (
                                <div className="viewer-primary-badge">
                                    Primary Photo
                                </div>
                            )}

                            {orderedPhotos.length > 1 && (
                                <div className="viewer-navigation">

                                    <button
                                        className="viewer-nav-btn"
                                        disabled={viewerIndex === 0}
                                        onClick={() =>
                                            setViewerIndex(prev => prev - 1)
                                        }
                                    >
                                        ‹
                                    </button>

                                    <span>
                                        {viewerIndex + 1} / {orderedPhotos.length}
                                    </span>

                                    <button
                                        className="viewer-nav-btn"
                                        disabled={
                                            viewerIndex === orderedPhotos.length - 1
                                        }
                                        onClick={() =>
                                            setViewerIndex(prev => prev + 1)
                                        }
                                    >
                                        ›
                                    </button>

                                </div>
                            )}

                        </Modal.Body>
                    </Modal>
                    {/* ── PROGRESS / RESULT STEP ── */}
                    {step === 'progress' && (
                        <div className="pv-result-section">

                            {!submitDone ? (
                                <>
                                    <div className="pv-result-badge">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
                                            <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Uploading Photos
                                    </div>


                                    <div className="pv-thumbs-grid">
                                        {croppedImages.map((img, i) => (
                                            <div key={i} className="pv-thumb-item">
                                                <img src={img.dataUrl} alt={`Photo ${i + 1}`} className="pv-thumb-img" />
                                                <div className={`pv-thumb-overlay ${uploadStatus[i] === 'done' ? 'done' : ''}`}>
                                                    <StatusIcon status={uploadStatus[i] || 'pending'} />
                                                </div>
                                                <span className="pv-thumb-label">Photo {i + 1}</span>
                                            </div>
                                        ))}
                                    </div>


                                    <div className="pv-upload-summary">
                                        {uploadStatus.filter(s => s === 'done').length} / {croppedImages.length} uploaded
                                    </div>

                                    {allUploaded && (
                                        <div className="pv-actions" style={{ marginTop: 16 }}>
                                            <button className="pv-btn-ghost" onClick={reset}>Cancel</button>
                                            <button
                                                className="pv-btn-primary"
                                                onClick={handleSubmitPhotos}
                                                disabled={submitting}
                                            >
                                                {submitting ? 'Saving…' : (
                                                    <>
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                                                            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        Save to Profile
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="pv-result-badge">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Profile Updated!
                                    </div>

                                    <div className="pv-thumbs-grid">
                                        {croppedImages.map((img, i) => (
                                            <div key={i} className="pv-thumb-item">
                                                <img src={img.dataUrl} alt={`Photo ${i + 1}`} className="pv-thumb-img" />
                                                <div className="pv-thumb-overlay done">
                                                    <StatusIcon status="done" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button className="pv-new-upload" onClick={reset} style={{ marginTop: 20 }}>
                                        Upload different photos
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default ImageUpload;